import { useState, useEffect } from "react";
import { X, ShieldAlert, CheckCircle, Clock } from "lucide-react";
import { getSOSReportadosPendientes, SOSReportado } from "../../../api/sosService";
import { useAuth } from "../../../context/AuthContext";

interface SOSReportadosModalProps {
  isOpen: boolean;
  onClose: () => void;
  reloadKey?: number;
}

export function SOSReportadosModal({ isOpen, onClose, reloadKey }: SOSReportadosModalProps) {
  const { session } = useAuth();
  const [reportes, setReportes] = useState<SOSReportado[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchReportes = async () => {
    if (!session?.unidad?.id) return;
    setLoading(true);
    try {
      const data = await getSOSReportadosPendientes(session.unidad.id);
      setReportes(data);
    } catch (error) {
      console.error("Error al cargar reportes SOS pendientes:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchReportes();
    }
  }, [isOpen, reloadKey]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[80] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[90vh] border-2 border-red-500">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b bg-red-500 text-white">
          <div className="flex items-center gap-3">
            <ShieldAlert size={28} className="animate-pulse" />
            <h2 className="text-xl font-bold">
              ¡EMERGENCIAS SOS PENDIENTES!
            </h2>
            <span className="bg-white text-red-600 text-xs font-bold px-2 py-1 rounded-full">
              {reportes.length}
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-red-100 hover:text-white transition"
            title="Cerrar"
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 overflow-y-auto flex-1 bg-gray-50">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-red-500"></div>
            </div>
          ) : reportes.length === 0 ? (
            <div className="text-center py-12 text-gray-500 bg-white rounded-lg border border-dashed border-green-300 flex flex-col items-center gap-3">
              <CheckCircle size={48} className="text-green-500" />
              <p className="text-lg font-medium text-green-700">No hay emergencias pendientes en este momento.</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {reportes.map((rep) => (
                <div key={rep.sre_codigo} className="bg-white rounded-lg shadow-md border-l-4 border-l-red-500 overflow-hidden relative group">
                  <div className="absolute top-0 right-0 bg-red-100 text-red-800 text-xs font-bold px-2 py-1 rounded-bl-lg border-b border-l border-red-200">
                    Cod: {rep.sre_codigo}
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-bold text-red-600 uppercase flex items-center gap-2">
                          <ShieldAlert size={18} />
                          {rep.sre_sos_asunto_nombre}
                        </h3>
                        <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                          <Clock size={12} /> {rep.sre_fecha}
                        </p>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-md p-3 grid grid-cols-2 gap-2 text-sm">
                      <div className="col-span-2 sm:col-span-1">
                        <span className="block text-xs text-gray-500 font-medium">Torre</span>
                        <span className="font-semibold text-gray-800">{rep.sre_torre_nombre || "-"}</span>
                      </div>
                      <div className="col-span-2 sm:col-span-1">
                        <span className="block text-xs text-gray-500 font-medium">Apto/Oficina</span>
                        <span className="font-semibold text-gray-800">{rep.sre_apartamento_nombre || "-"}</span>
                      </div>
                      <div className="col-span-2 mt-1">
                        <span className="block text-xs text-gray-500 font-medium">Residente Reporta</span>
                        <span className="font-semibold text-gray-800 uppercase">{rep.sre_usuario_residente_nombre || "-"}</span>
                      </div>
                    </div>
                  </div>
                  
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
